import React from 'react';
import dayjs from 'dayjs';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LabelList
} from 'recharts';
import Typography from "@mui/material/Typography";
import {UserActivityTypes} from "../../types";
import {Box} from "@mui/material";
import {getWeekDisplay} from "../../helpers/weekYearDisplayFormat";

interface Props{
    data: UserActivityTypes.TeamUserWeeklyActivityRecords[]
}

interface ChartDataPoint {
    date: string;
    value: string;
}

const transformToChartData = (data: UserActivityTypes.TeamUserWeeklyActivityRecords[]): ChartDataPoint[] => {
    if (!data || data.length === 0) {
        return [];
    }

    // Get all unique weeks from the data
    const allWeeks = new Set<string>();
    data.forEach(user => {
        user.weeklyActivity?.forEach(week => {
            allWeeks.add(week.weekYear);
        });
    });

    const sortedWeeks = Array.from(allWeeks).sort((a, b) => parseInt(a) - parseInt(b));

    // Calculate total active days for each week across all users
    const chartData: ChartDataPoint[] = sortedWeeks.map(weekYear => {
        let totalActiveDays = 0;

        data.forEach(user => {
            const weekActivity = user.weeklyActivity?.find(week => week.weekYear === weekYear);
            if (weekActivity) {
                totalActiveDays += weekActivity.activeDaysInWeek || 0;
            }
        });

        let totalActiveDaysForWeek = data?.length * 7;
        let activePercentage = (totalActiveDays / totalActiveDaysForWeek) * 100;

        return {
            date: getWeekDisplay(weekYear, sortedWeeks),
            value: Math.round(activePercentage).toString()
        };
    });

    return chartData;
}

const TeamWeeklyActiveLineChart : React.FC<Props> = ({data}) => {
    const chartData: ChartDataPoint[] = transformToChartData(data);

    // Calculate max value for Y-axis domain
    const maxValue = Math.max(...chartData.map(item => parseInt(item.value)));
    const yAxisMax = Math.ceil(maxValue * 1.1); // Add 10% padding

    return (
        <div style={{ width: '100%', height: 300 }}>
            {chartData.length === 0 ? (

                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', margin: 'auto' }}>
                    <Typography align={"center"} fontSize={16} fontWeight={600}>
                        No data available
                    </Typography>
                </Box>
            ) : (
                <>
                    <Typography align={"center"} fontWeight={400} fontSize={16} mb={2}>
                        % of Total Days Active Per Week
                    </Typography>
                    <ResponsiveContainer width="100%" height="80%">
                        <LineChart
                            data={chartData}
                        >
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 12 }}
                                padding={{ left: 30, right: 30 }}
                            />
                            <YAxis
                                tick={false} // Hide all tick texts
                                tickLine={false} // Hide tick lines
                                domain={[0, yAxisMax]}
                            />
                            <Tooltip
                                formatter={(value) => [Number(value)+'%', '% of Days Active']}
                                labelFormatter={(label) => `Week: ${label}`}
                            />
                            <Line
                                type="linear"
                                dataKey="value"
                                stroke="#ccc"
                                dot={{
                                    r: 8,
                                    fill: '#11A7E2',
                                    stroke: '#ccc',
                                    strokeWidth: 1
                                }}
                            >
                                <LabelList
                                    dataKey="value"
                                    position="top"
                                    formatter={(value : any) => Number(value)+'%'}
                                    style={{ fill: '#000', fontWeight: 400, fontSize: 14, transform: 'translateY(-8px)'}}
                                />
                            </Line>
                        </LineChart>
                    </ResponsiveContainer>
                </>
            )}
        </div>
    );
};

export default TeamWeeklyActiveLineChart;